import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Globe, BarChart2, DollarSign, MousePointer, Clock, TrendingUp } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const WebContentAnalysisPage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/web-content-analysis`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Web Content Analysis Tools: Measure Content Performance | Free Calculators</title>
        <meta name="description" content="Analyze your web content performance with our free calculators. Measure bounce rates, read rates, conversion rates & more. Optimize your content strategy." />
        <meta name="keywords" content="web content analysis, bounce rate calculator, blog post read rate, content ROI, landing page conversion rate" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Web Content Analysis Tools: Optimize Your Content Strategy" />
        <meta property="og:description" content="Analyze and improve your web content performance with our free calculators. Track key metrics and optimize your content strategy." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Web Content Analysis Tools: Optimize Your Content Strategy" />
        <meta property="twitter:description" content="Analyze and improve your web content performance with our free calculators. Track key metrics and optimize your content strategy." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Web Content Analysis</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Content Performance Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Bounce Rate" 
            description="Measure how many visitors read your blog posts to completion" 
            link="/calculators/bounce-rate" 
            icon={Globe}
          />
          <CalculatorCard 
            title="Blog Post Read Rate" 
            description="Calculate the average cost to acquire a website visitor" 
            link="/calculators/blog-post-read-rate" 
            icon={BarChart2}
          />
          <CalculatorCard 
            title="Cost Per Website Visit" 
            description="Measure the effectiveness of your landing pages" 
            link="/calculators/cost-per-website-visit" 
            icon={DollarSign}
          />
          <CalculatorCard 
            title="Landing Page Conversion Rate" 
            description="Calculate the value of time spent on your website" 
            link="/calculators/landing-page-conversion-rate" 
            icon={MousePointer}
          />
          <CalculatorCard 
            title="Time on Site Value" 
            description="Measure the return on investment for your content marketing efforts" 
            link="/calculators/time-on-site-value" 
            icon={Clock}
          />
          <CalculatorCard 
            title="Content Marketing ROI" 
            description="Calculate the return on investment for your social media campaigns" 
            link="/calculators/content-marketing-roi" 
            icon={TrendingUp}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Web Content Analysis</h2>
        <p className="text-gray-600 mb-4">
          Web content analysis helps you understand how visitors interact with your content, enabling you to optimize for better engagement and conversion rates. Use these calculators to measure and improve your content performance.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default WebContentAnalysisPage;
